import React from 'react';
import {API} from "aws-amplify";
import {Icons, toast, ToastContainer} from "react-toastify";
import Select from "react-select";
import NoteAddIcon from '@mui/icons-material/NoteAdd';
import FileCopyIcon from '@mui/icons-material/FileCopy';
import _ from "lodash";

import InputCell from "@/common/form/InputCell";

const MAX_RULE_NAME_LENGTH = 255;
const MODAL_TOAST_CONTAINER_ID = "modal-toast";
const ERROR_AUTO_CLOSE_MS = 5000;
const SUCCESS_AUTO_CLOSE_MS = 2000;

// Edit Rule toast accessibility (EN-21346, EN-21359, EN-21680):
// - Every toast is role="alert" and the container's live region is off, so the alert is the only
//   thing that announces the message, and it is announced as an alert (EN-21680).
// - Focus moves to the close button in the same render that inserts the alert (EN-21346), so the
//   screen reader reads the alert once, as the focus context (EN-21359). The previous retry loop
//   focused ~100ms later, after the alert had already been announced, so it was read twice.
// - No progress bar ("notification timer") and no container label ("Notification Alt+T region").

// Replaces react-toastify's close button so the toast can take focus as it opens, close on Escape
// without closing the Edit Rule modal behind it, and hand focus back when it closes.
function ModalToastCloseButton({closeToast, theme, trigger, autoCloseAfterFocusLeaves}) {
    const buttonRef = React.useRef(null);

    const close = () => {
        const toastElement = buttonRef.current?.closest('.Toastify__toast');
        // WCAG 2.4.3: return focus to the control that raised the toast, unless the user moved on
        if (trigger?.isConnected && toastElement?.contains(document.activeElement)) {
            trigger.focus();
        }
        closeToast(true);
    };
    const closeRef = React.useRef(close);
    closeRef.current = close;

    React.useLayoutEffect(() => {
        const toastElement = buttonRef.current.closest('.Toastify__toast');
        let timer = null;
        let pointerInside = false;

        const stopTimer = () => clearTimeout(timer);
        // WCAG 2.2.1: an error only counts down once both focus and pointer have left it
        const startTimer = () => {
            clearTimeout(timer);
            if (autoCloseAfterFocusLeaves) {
                timer = setTimeout(() => closeToast(true), autoCloseAfterFocusLeaves);
            }
        };
        const onKeyDown = (event) => {
            if (event.key === 'Escape') {
                event.stopPropagation();  // Otherwise MUI's Modal closes the whole Edit Rule dialog
                closeRef.current();
            }
        };
        const onFocusOut = (event) => {
            // A null relatedTarget while the document has lost focus is a window switch, not leaving
            const focusLeftToast = event.relatedTarget
                ? !toastElement.contains(event.relatedTarget)
                : document.hasFocus();
            if (focusLeftToast && !pointerInside) {
                startTimer();
            }
        };
        const onMouseEnter = () => {
            pointerInside = true;
            stopTimer();
        };
        const onMouseLeave = () => {
            pointerInside = false;
            if (!toastElement.contains(document.activeElement)) {
                startTimer();
            }
        };

        toastElement.addEventListener('keydown', onKeyDown);
        toastElement.addEventListener('focusin', stopTimer);
        toastElement.addEventListener('focusout', onFocusOut);
        toastElement.addEventListener('mouseenter', onMouseEnter);
        toastElement.addEventListener('mouseleave', onMouseLeave);
        buttonRef.current.focus();

        return () => {
            stopTimer();
            toastElement.removeEventListener('keydown', onKeyDown);
            toastElement.removeEventListener('focusin', stopTimer);
            toastElement.removeEventListener('focusout', onFocusOut);
            toastElement.removeEventListener('mouseenter', onMouseEnter);
            toastElement.removeEventListener('mouseleave', onMouseLeave);
        };
    }, []);

    return (
        <button
            ref={buttonRef}
            type="button"
            className={`Toastify__close-button Toastify__close-button--${theme}`}
            aria-label="Close"
            onClick={close}
        >
            <svg aria-hidden="true" viewBox="0 0 14 16">
                <path fillRule="evenodd" d="M7.71 8.23l3.75 3.75-1.48 1.48-3.75-3.75-3.75 3.75L1 11.98l3.75-3.75L1 4.48 2.48 3l3.75 3.75L9.98 3l1.48 1.48-3.75 3.75z" />
            </svg>
        </button>
    );
}

const modalToastOptions = (type, trigger) => ({
    containerId: MODAL_TOAST_CONTAINER_ID,
    position: "top-center",
    type,
    role: "alert",
    hideProgressBar: true,  // Progress bar is announced as "notification timer" (EN-21359)
    closeOnClick: false,  // Require explicit close button click for accessibility
    draggable: false,  // Prevent accidental dismissal
    icon: ({theme}) => Icons[type]({theme, type, "aria-hidden": true}),  // Decorative; the text says it
    // Errors close 5s after the user leaves them; success closes the modal after 2s
    autoClose: type === "success" ? SUCCESS_AUTO_CLOSE_MS : false,
    closeButton: (props) => (
        <ModalToastCloseButton
            {...props}
            trigger={type === "error" ? trigger : null}  // On success the modal closes and MUI restores focus
            autoCloseAfterFocusLeaves={type === "error" ? ERROR_AUTO_CLOSE_MS : null}
        />
    )
});

function ModalToastContainer() {
    const wrapperRef = React.useRef(null);

    // EN-21680: react-toastify hardcodes aria-live="polite" on its container with no prop to override it.
    // We set it to "off" because each toast has role="alert" (for errors) or role="status" (for success),
    // which provides implicit aria-live behavior. This prevents duplicate announcements while ensuring
    // validation messages are properly identified as alerts to screen readers (WCAG 4.1.3).
    React.useEffect(() => {
        const container = wrapperRef.current?.querySelector('.Toastify');
        if (container) {
            container.setAttribute('aria-live', 'off');
            // Remove aria-label from container to prevent "Notification Alt+T region" announcement
            container.removeAttribute('aria-label');
        }
    }, []);

    return (
        <div ref={wrapperRef}>
            <ToastContainer containerId={MODAL_TOAST_CONTAINER_ID} position="top-center" theme="dark" />
        </div>
    );
}

export default function AddEditRule(props){
    const { onCancel, onSuccess, existingRule, rules} = props;

    const titleRef = React.useRef(null);

    const [step, setStep] = React.useState(1);
    const [mode, setMode] = React.useState('scratch');
    const [selectedRule, setSelectedRule] = React.useState(null);
    const [selectedVersion, setSelectedVersion] = React.useState(null);
    const [versions, setVersions] = React.useState([]);
    const [loadingVersions, setLoadingVersions] = React.useState(false);
    const [ruleName, setRuleName] = React.useState('');
    const [activeVersionId, setActiveVersionId] = React.useState(null);

    const toastIdRef = React.useRef(null);
    const toastTriggerRef = React.useRef(null);

    // EN-21346: Visual focus indicator for the toast close button, which takes focus when a toast
    // opens (WCAG 2.4.7 Focus Visible)
    React.useEffect(() => {
        const style = document.createElement('style');
        style.textContent = '.Toastify__close-button:focus-visible { outline: 3px solid #005fcc; outline-offset: 2px; border-radius: 2px; }';
        document.head.appendChild(style);
        return () => {
            document.head.removeChild(style);
        };
    }, []);

    const showModalToast = (message, type, options = {}) => {
        // If a toast replaces another while focus is still inside it, keep the original trigger
        const active = document.activeElement;
        if (!active?.closest('.Toastify')) {
            toastTriggerRef.current = active === document.body ? null : active;
        }
        // One message at a time, so focus and the screen reader only ever deal with the latest one
        if (toastIdRef.current != null && toast.isActive(toastIdRef.current, MODAL_TOAST_CONTAINER_ID)) {
            toast.dismiss({id: toastIdRef.current, containerId: MODAL_TOAST_CONTAINER_ID});
        }
        toastIdRef.current = toast(message, {
            ...modalToastOptions(type, toastTriggerRef.current),
            ...options
        });
    };

    const showError = (message) => showModalToast(message, "error");

    const showSuccess = (response) => showModalToast("Rule Saved!", "success", {
        // Close modal and refresh after toast is dismissed
        onClose: () => onSuccess(response)
    });

    const handleNext = () => {
        const name = titleRef.current.value.trim();
        if (!name) {
            showError("Rule name cannot be empty");
            return;
        }
        if (name.length > MAX_RULE_NAME_LENGTH) {
            showError(`Rule name limit is ${MAX_RULE_NAME_LENGTH} characters`);
            return;
        }
        setRuleName(name);
        setStep(2);
    };

    const handleBack = () => {
        setStep(1);
    };

    const handleRuleChange = (option) => {
        setSelectedRule(option);
        setSelectedVersion(null);
        setVersions([]);
        setActiveVersionId(null);
        if (!option) {
            return;
        }
        setLoadingVersions(true);

        // Fetch both the rule details (for activeRuleVersion) and versions in parallel
        Promise.all([
            API.get('Core', `/api/v1/rule/${option.value}`),
            API.get('Core', `/api/v1/rule/${option.value}/version`)
        ])
            .then(([ruleDetails, versionsResponse]) => {
                setVersions(versionsResponse);

                // Find active version by comparing with rule's activeRuleVersion
                const activeVersionIdFromRule = ruleDetails.activeRuleVersion?.id;
                setActiveVersionId(activeVersionIdFromRule);

                if (activeVersionIdFromRule) {
                    const activeVersion = versionsResponse.find(v => v.id === activeVersionIdFromRule);
                    if (activeVersion) {
                        setSelectedVersion({
                            value: activeVersion.id,
                            label: `Version ${activeVersion.version} (Active)`
                        });
                    }
                }
                setLoadingVersions(false);
            })
            .catch(error => {
                showError("Could not load versions");
                setVersions([]);
                setLoadingVersions(false);
            });
    };

    const handleCreate = (nameOverride) => {
        // If nameOverride is an event object (has nativeEvent), ignore it and use ruleName
        const name = (nameOverride && !nameOverride.nativeEvent) ? nameOverride : ruleName;
        if (mode === 'scratch') {
            API.post('Core', '/api/v1/rule', {body: {name}})
                .then(showSuccess,
                error => {
                    showError("Could not create rule: " + error.response.data.message);
                });
        } else {
            API.post('Core', '/api/v1/rule/copy', {
                body: {name, sourceRuleVersionId: selectedVersion.value}
            })
                .then(showSuccess,
                error => {
                    showError("Could not create rule: " + error.response.data.message);
                });
        }
    };

    const saveService = () => {
        const name = titleRef.current.value.trim();
        if (!name) {
            showError("Rule name cannot be empty");
            return;
        }
        if (name.length > MAX_RULE_NAME_LENGTH) {
            showError(`Rule name limit is ${MAX_RULE_NAME_LENGTH} characters`);
            return;
        }
        API.put('Core', '/api/v1/rule/' + existingRule.id, {body: {name: name}})
            .then(showSuccess,
            error => {
                showError("Could not update rule: " + error.response.data.message);
            });
    };

    const isCreateDisabled = mode === 'copy' && (!selectedRule || !selectedVersion);

    const ruleOptions = (rules || []).map(r => ({
        value: r.id,
        label: r.name + (r.status === 'DISABLED' ? ' (Inactive)' : '')
    }));

    // Sort versions from newest to oldest (descending by version number)
    const sortedVersions = _.reverse(_.sortBy(versions, v => v.version));
    const versionOptions = sortedVersions.map(v => ({
        value: v.id,
        label: `Version ${v.version}${v.id === activeVersionId ? ' (Active)' : ''}`
    }));

    // Custom styles for react-select to match theme and appear above modal
    const selectStyles = {
        control: (base) => ({
            ...base,
            backgroundColor: 'var(--colorBackgroundPrimary)',
            borderColor: 'var(--colorSeparator)',
            '&:hover': {
                borderColor: 'var(--colorSeparator)',
            },
        }),
        input: (base) => ({
            ...base,
            color: 'var(--colorTitle)',
        }),
        singleValue: (base) => ({
            ...base,
            color: 'var(--colorTitle)',
        }),
        placeholder: (base) => ({
            ...base,
            color: 'var(--colorTitlePlaceholder)',
        }),
        menuPortal: (base) => ({
            ...base,
            zIndex: 9999,
        }),
        menu: (base) => ({
            ...base,
            backgroundColor: 'var(--colorBackgroundPrimary)',
            borderColor: 'var(--colorSeparator)',
        }),
        menuList: (base) => ({
            ...base,
            backgroundColor: 'var(--colorBackgroundPrimary)',
            padding: 0,
        }),
        option: (base, state) => ({
            ...base,
            backgroundColor: state.isFocused
                ? 'var(--colorTintTranslucent)'
                : state.isSelected
                    ? 'var(--colorTint)'
                    : 'var(--colorBackgroundPrimary)',
            color: state.isSelected ? 'var(--colorTitleSecondary)' : 'var(--colorTitle)',
            cursor: 'pointer',
            '&:active': {
                backgroundColor: 'var(--colorTint)',
            },
        }),
    };

    if (existingRule) {
        return (
            <div>
                <ModalToastContainer />
                <div className="content">
                    <div className="tableView">
                        <div className="tableSection sectionName">
                            <p style={{ fontSize: '0.75em', color: 'var(--colorTitle)', marginBottom: '6px', marginTop: '-4px' }}>
                                Fields marked with <span style={{ color: 'red' }}>*</span> are required.
                            </p>
                            <InputCell
                                placeholder={"Rule Name"}
                                defaultValue={existingRule.name}
                                required
                                id="ruleName"
                                label="Rule Name"
                                inputRef={titleRef}
                                maxLength={MAX_RULE_NAME_LENGTH}
                                showRequiredIndicator
                                blurErrorMessage="Rule Name cannot be empty."
                            />
                        </div>
                    </div>
                </div>
                <div className="actionBar">
                    <div className="actions">
                        <div
                            onClick={onCancel}
                            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onCancel(); } }}
                            className="button dismiss medium"
                            role="button"
                            tabIndex={0}
                        >
                            <div className="title">Cancel</div>
                        </div>
                        <div
                            onClick={saveService}
                            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); saveService(); } }}
                            className="button confirm medium"
                            role="button"
                            tabIndex={0}
                        >
                            <div className="title">Done</div>
                        </div>
                    </div>
                    <div className="separator"></div>
                </div>
            </div>
        );
    }


    if (step === 1) {
        return (
            <div>
                <ModalToastContainer />
                <div className="content">
                    <div className="tableView">
                        <div className="tableSection sectionName">
                            <p style={{ fontSize: '0.75em', color: 'var(--colorTitle)', marginBottom: '6px', marginTop: '-4px' }}>
                                Fields marked with <span style={{ color: 'red' }}>*</span> are required.
                            </p>
                            <InputCell
                                placeholder={"Rule Name"}
                                defaultValue={ruleName}
                                required
                                id="ruleName"
                                label="Rule Name"
                                inputRef={titleRef}
                                maxLength={MAX_RULE_NAME_LENGTH}
                                showRequiredIndicator
                                blurErrorMessage="Rule Name cannot be empty."
                            />
                        </div>
                    </div>
                </div>
                <div className="actionBar">
                    <div className="actions">
                        <div
                            onClick={onCancel}
                            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onCancel(); } }}
                            className="button dismiss medium"
                            role="button"
                            tabIndex={0}
                        >
                            <div className="title">Cancel</div>
                        </div>
                        <div
                            onClick={handleNext}
                            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleNext(); } }}
                            className="button confirm medium"
                            role="button"
                            tabIndex={0}
                        >
                            <div className="title">Next</div>
                        </div>
                    </div>
                    <div className="separator"></div>
                </div>
            </div>
        );
    }

    return (
        <div>
            <ModalToastContainer />
            <div className="content">
                <div className="tableView">
                    <div className="tableSection" style={{padding: '24px'}}>
                        <div style={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '28px'}}>
                            <div
                                onClick={() => setMode('scratch')}
                                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setMode('scratch'); } }}
                                role="button"
                                tabIndex={0}
                                aria-pressed={mode === 'scratch'}
                                style={{
                                    border: mode === 'scratch' ? '2px solid var(--colorTint)' : '2px solid var(--colorSeparator)',
                                    borderRadius: '8px',
                                    padding: '32px 24px',
                                    cursor: 'pointer',
                                    backgroundColor: mode === 'scratch' ? 'var(--colorTintTranslucent)' : 'var(--colorBackgroundSecondary)',
                                    transition: 'all 0.2s ease',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'flex-start',
                                    minHeight: '180px'
                                }}
                            >
                                <NoteAddIcon style={{fontSize: '56px', color: 'var(--colorSubtitle)', marginBottom: '16px'}} />
                                <div style={{fontSize: '17px', fontWeight: 500, color: 'var(--colorTitle)', marginBottom: '10px', lineHeight: '1.3'}}>
                                    Create from Scratch
                                </div>
                                <div style={{fontSize: '14px', color: 'var(--colorSubtitle)', lineHeight: '1.5'}}>
                                    Start with a blank rule
                                </div>
                            </div>

                            <div
                                onClick={() => setMode('copy')}
                                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setMode('copy'); } }}
                                role="button"
                                tabIndex={0}
                                aria-pressed={mode === 'copy'}
                                style={{
                                    border: mode === 'copy' ? '2px solid var(--colorTint)' : '2px solid var(--colorSeparator)',
                                    borderRadius: '8px',
                                    padding: '32px 24px',
                                    cursor: 'pointer',
                                    backgroundColor: mode === 'copy' ? 'var(--colorTintTranslucent)' : 'var(--colorBackgroundSecondary)',
                                    transition: 'all 0.2s ease',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'flex-start',
                                    minHeight: '180px'
                                }}
                            >
                                <FileCopyIcon style={{fontSize: '56px', color: 'var(--colorSubtitle)', marginBottom: '16px'}} />
                                <div style={{fontSize: '17px', fontWeight: 500, color: 'var(--colorTitle)', marginBottom: '10px', lineHeight: '1.3'}}>
                                    Copy Existing Rule
                                </div>
                                <div style={{fontSize: '14px', color: 'var(--colorSubtitle)', lineHeight: '1.5'}}>
                                    Duplicate a rule as a starting point
                                </div>
                            </div>
                        </div>

                        {mode === 'copy' && (
                            <>
                                <div style={{marginBottom: '16px'}}>
                                    <label style={{display: 'block', marginBottom: '6px', marginTop: '-4px', fontSize: '13px', fontWeight: 500, color: 'var(--colorSubtitle)'}}>
                                        Existing Rule
                                    </label>
                                    <Select
                                        className="selectCell"
                                        classNamePrefix="selectCell"
                                        options={ruleOptions}
                                        value={selectedRule}
                                        onChange={handleRuleChange}
                                        isClearable
                                        placeholder="- Select a rule -"
                                        isSearchable
                                        styles={selectStyles}
                                        menuPortalTarget={document.body}
                                        menuPosition="fixed"
                                        maxMenuHeight={300}
                                        filterOption={(option, inputValue) => {
                                            if (!inputValue) return true;
                                            return option.label.toLowerCase().includes(inputValue.toLowerCase());
                                        }}
                                    />
                                </div>

                                <div>
                                    <label style={{display: 'block', marginBottom: '6px', marginTop: '-4px', fontSize: '13px', fontWeight: 500, color: 'var(--colorSubtitle)'}}>
                                        Version
                                    </label>
                                    <Select
                                        className="selectCell"
                                        classNamePrefix="selectCell"
                                        options={versionOptions}
                                        value={selectedVersion}
                                        onChange={setSelectedVersion}
                                        isDisabled={!selectedRule}
                                        isLoading={loadingVersions}
                                        placeholder="- Select a version -"
                                        styles={selectStyles}
                                        menuPortalTarget={document.body}
                                        menuPosition="fixed"
                                        maxMenuHeight={300}
                                        filterOption={(option, inputValue) => {
                                            if (!inputValue) return true;
                                            return option.label.toLowerCase().includes(inputValue.toLowerCase());
                                        }}
                                    />
                                </div>
                            </>
                        )}
                    </div>
                </div>
            </div>
            <div className="actionBar">
                <div className="actions">
                    <div
                        onClick={handleBack}
                        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleBack(); } }}
                        className="button dismiss medium"
                        role="button"
                        tabIndex={0}
                    >
                        <div className="title">Back</div>
                    </div>
                    <div
                        onClick={isCreateDisabled ? undefined : handleCreate}
                        onKeyDown={(e) => { if (!isCreateDisabled && (e.key === 'Enter' || e.key === ' ')) { e.preventDefault(); handleCreate(); } }}
                        className={`button confirm medium ${isCreateDisabled ? 'disabled' : ''}`}
                        style={isCreateDisabled ? {opacity: 0.5, pointerEvents: 'none'} : {}}
                        role="button"
                        tabIndex={isCreateDisabled ? -1 : 0}
                        aria-disabled={isCreateDisabled}
                    >
                        <div className="title">Create Rule</div>
                    </div>
                </div>
                <div className="separator"></div>
            </div>
        </div>
    );
}
